import { MessageRole } from "@src/gql/graphql";
import { z } from "zod";

export const agentChatRoleSchema = z.enum(["user", "assistant", "system"]);

export type AgentChatRole = z.infer<typeof agentChatRoleSchema>;

/** API `role` is a string. Accept only `MessageRole` values. */
export const wireChatRoleSchema = z
	.string()
	.transform((value) => value.toUpperCase())
	.pipe(z.nativeEnum(MessageRole))
	.transform((role): AgentChatRole => {
		switch (role) {
			case MessageRole.User:
				return "user";
			case MessageRole.Assistant:
				return "assistant";
			case MessageRole.System:
				return "system";
		}
	});

export const agentChatTextPartSchema = z.object({
	type: z.literal("text"),
	text: z.string(),
});

export const agentChatReasoningPartSchema = z.object({
	type: z.literal("reasoning"),
	text: z.string(),
	done: z.boolean(),
	durationMs: z.number().optional(),
});

export const agentChatToolCallPartSchema = z.object({
	type: z.literal("tool-call"),
	toolCallId: z.string().optional(),
	toolName: z.string(),
	state: z.enum([
		"input-streaming",
		"running",
		"awaiting_approval",
		"completed",
	]),
	input: z.unknown().optional(),
});

export const agentChatToolResultPartSchema = z.object({
	type: z.literal("tool-result"),
	toolCallId: z.string().optional(),
	toolName: z.string(),
	output: z.unknown().optional(),
});

export const agentChatActionReceiptPartSchema = z.object({
	type: z.literal("action-receipt"),
	status: z.enum(["success", "denied", "error"]),
	title: z.string(),
	description: z.string().optional(),
	toolCallId: z.string().optional(),
	toolName: z.string().optional(),
});

export const agentChatChoiceOptionSchema = z.object({
	id: z.string(),
	label: z.string(),
	description: z.string().optional(),
});

export const agentChatChoiceRequestPartSchema = z.object({
	type: z.literal("choice-request"),
	choiceId: z.string(),
	prompt: z.string(),
	options: z.array(agentChatChoiceOptionSchema),
});

export const agentChatChoiceResponsePartSchema = z.object({
	type: z.literal("choice-response"),
	choiceId: z.string(),
	optionId: z.string(),
	label: z.string(),
});

export const agentChatApprovalRequestPartSchema = z.object({
	type: z.literal("approval-request"),
	approvalId: z.string(),
	toolCallId: z.string(),
	toolName: z.string(),
	input: z.unknown().optional(),
});

export const agentChatApprovalResponsePartSchema = z.object({
	type: z.literal("approval-response"),
	approvalId: z.string(),
	approved: z.boolean(),
	reason: z.string().optional(),
});

export const agentChatGeneratedImagePartSchema = z.object({
	type: z.literal("generated-image"),
	modelId: z.string(),
	modelLabel: z.string().optional(),
	mediaType: z.string(),
	data: z.string(),
	dataKind: z.enum(["base64", "url"]),
	status: z.enum(["pending", "ready", "error"]),
	errorMessage: z.string().optional(),
});

export const agentChatAssistantPartSchema = z.discriminatedUnion("type", [
	agentChatTextPartSchema,
	agentChatReasoningPartSchema,
	agentChatToolCallPartSchema,
	agentChatToolResultPartSchema,
	agentChatActionReceiptPartSchema,
	agentChatChoiceRequestPartSchema,
	agentChatChoiceResponsePartSchema,
	agentChatApprovalRequestPartSchema,
	agentChatApprovalResponsePartSchema,
	agentChatGeneratedImagePartSchema,
]);

export const agentChatAttachmentKindSchema = z.enum(["image", "text"]);

export const agentChatAttachmentSchema = z.object({
	id: z.string(),
	name: z.string(),
	mediaType: z.string(),
	kind: agentChatAttachmentKindSchema,
	data: z.string(),
});

export const agentChatPersonMentionSchema = z.object({
	id: z.string(),
	displayName: z.string(),
	email: z.string().optional(),
	avatarUrl: z.string().nullish(),
	isOnline: z.boolean().optional(),
});

export const agentChatReferenceSchema = z.object({
	id: z.string(),
	title: z.string(),
	key: z.string().nullish(),
});

export const agentChatUserMessageSchema = z.object({
	id: z.string(),
	role: z.literal("user"),
	content: z.string(),
	createdAt: z.number(),
	attachments: z.array(agentChatAttachmentSchema).optional(),
	mentions: z.array(agentChatPersonMentionSchema).optional(),
	peopleContext: z.string().optional(),
	references: z.array(agentChatReferenceSchema).optional(),
	referenceContext: z.string().optional(),
});

export const agentChatAssistantMessageSchema = z.object({
	id: z.string(),
	role: z.literal("assistant"),
	parts: z.array(agentChatAssistantPartSchema),
	createdAt: z.number(),
});

export const agentChatMessageSchema = z.discriminatedUnion("role", [
	agentChatUserMessageSchema,
	agentChatAssistantMessageSchema,
]);

export const agentChatStatusSchema = z.enum([
	"idle",
	"submitting",
	"awaiting_choice",
	"awaiting_approval",
	"error",
]);

export type AgentChatTextPart = z.infer<typeof agentChatTextPartSchema>;
export type AgentChatReasoningPart = z.infer<
	typeof agentChatReasoningPartSchema
>;
export type AgentChatToolCallPart = z.infer<typeof agentChatToolCallPartSchema>;
export type AgentChatToolResultPart = z.infer<
	typeof agentChatToolResultPartSchema
>;
export type AgentChatActionReceiptPart = z.infer<
	typeof agentChatActionReceiptPartSchema
>;
export type AgentChatChoiceOption = z.infer<typeof agentChatChoiceOptionSchema>;
export type AgentChatChoiceRequestPart = z.infer<
	typeof agentChatChoiceRequestPartSchema
>;
export type AgentChatChoiceResponsePart = z.infer<
	typeof agentChatChoiceResponsePartSchema
>;
export type AgentChatApprovalRequestPart = z.infer<
	typeof agentChatApprovalRequestPartSchema
>;
export type AgentChatApprovalResponsePart = z.infer<
	typeof agentChatApprovalResponsePartSchema
>;
export type AgentChatGeneratedImagePart = z.infer<
	typeof agentChatGeneratedImagePartSchema
>;
export type AgentChatAssistantPart = z.infer<
	typeof agentChatAssistantPartSchema
>;
export type AgentChatAttachmentKind = z.infer<
	typeof agentChatAttachmentKindSchema
>;
export type AgentChatAttachment = z.infer<typeof agentChatAttachmentSchema>;
export type AgentChatPersonMention = z.infer<
	typeof agentChatPersonMentionSchema
>;
export type AgentChatReference = z.infer<typeof agentChatReferenceSchema>;
export type AgentChatUserMessage = z.infer<typeof agentChatUserMessageSchema>;
export type AgentChatAssistantMessage = z.infer<
	typeof agentChatAssistantMessageSchema
>;
export type AgentChatMessage = z.infer<typeof agentChatMessageSchema>;
export type AgentChatStatus = z.infer<typeof agentChatStatusSchema>;
